import type { Point, SceneNode } from "../scene/model";
import { invertAffine, transformPoint } from "../geometry/affine";
import { hitFrameHandle } from "./geometry";
import { deformNode, warpQuad } from "./deformation";
import { multiply, nodeMatrix, transformNativeNode } from "./transforms";
import { limitEdgeWarp, zeroEdges } from "../geometry/edge-warp";

/** Clockwise: corner, following edge, corner, following edge, ... */
export function deformationPoints(node: SceneNode) {
  const quad = warpQuad(node),
    matrix = nodeMatrix(node);
  return quad
    .flatMap((p, i) => [
      p,
      {
        x:
          (p.x + quad[(i + 1) % 4]!.x) / 2 +
          (node.geometry.warpEdges?.[i]?.x ?? 0),
        y:
          (p.y + quad[(i + 1) % 4]!.y) / 2 +
          (node.geometry.warpEdges?.[i]?.y ?? 0),
      },
    ])
    .map((p) => transformPoint(p, matrix));
}

export function hitDeformationHandle(
  node: SceneNode,
  point: Point,
  zoom: number,
  touch: boolean,
  editing: boolean,
) {
  if (!editing) {
    // Ctrl targets the VISIBLE ordinary controls, including their entire hit area.
    const ordinary = hitFrameHandle(nodeMatrix(node), point, zoom, touch);
    const index = [0, 1, 2, 7, 3, 6, 5, 4][ordinary] ?? -1;
    return node.kind === "text" && index % 2 === 0 ? -1 : index;
  }
  return (
    deformationPoints(node)
      .map((p, index) => ({
        index,
        distance: Math.hypot(p.x - point.x, p.y - point.y) * zoom,
      }))
      .filter((p) => p.distance <= (touch ? 23 : 10))
      .sort((a, b) => a.distance - b.distance)[0]?.index ?? -1
  );
}

/** Displacement is measured in the unchanged local frame captured on pointerdown. */
export function deformHandle(
  node: SceneNode,
  handle: number,
  delta: Point,
  symmetric = false,
  bend = false,
) {
  if (handle < 0 || handle > 7) return node;
  const corner = Math.floor(handle / 2),
    edge = handle % 2 === 1;
  if (bend && edge && node.interaction.role !== "inputField") {
    if (Math.abs(delta.x) + Math.abs(delta.y) < 1e-12) return node;
    const q = warpQuad(node),
      previous = node.geometry.warpEdges ?? zeroEdges(),
      next = previous.map((p) => ({ ...p }));
    next[corner]!.x += delta.x;
    next[corner]!.y += delta.y;
    if (symmetric) {
      next[(corner + 2) % 4]!.x -= delta.x;
      next[(corner + 2) % 4]!.y -= delta.y;
    }
    const changed = deformNode(node, [...q]);
    return {
      ...changed,
      geometry: {
        ...changed.geometry,
        warpEdges: limitEdgeWarp(q, previous, next),
      },
    };
  }
  const shift = !edge
    ? delta
    : corner % 2 === 0
      ? { x: delta.x, y: 0 }
      : { x: 0, y: delta.y };
  if (Math.abs(shift.x) < 1e-12 && Math.abs(shift.y) < 1e-12) return node;
  if (node.kind === "text" && !bend) {
    if (!edge) return node;
    const horizontal = corner % 2 === 0,
      value = horizontal ? shift.x : shift.y;
    const sign = corner === 0 || corner === 3 ? -1 : 1,
      matrix = nodeMatrix(node),
      inverse = invertAffine(matrix);
    if (!inverse) return node;
    const local: [number, number, number, number, number, number] = horizontal
      ? [1, 0, value * sign, 1, value / 2, 0]
      : [1, value * sign, 0, 1, 0, value / 2];
    return transformNativeNode(
      node,
      multiply(multiply(matrix, local), inverse),
    );
  }
  const quad = warpQuad(node).map((p) => ({ ...p }));
  for (const i of edge ? [corner, (corner + 1) % 4] : [corner]) {
    quad[i]!.x += shift.x;
    quad[i]!.y += shift.y;
  }
  if (!edge && symmetric) {
    quad[(corner + 2) % 4]!.x -= shift.x;
    quad[(corner + 2) % 4]!.y -= shift.y;
  }
  return deformNode(node, quad);
}
